import { useMemo } from "react"
import { get } from "lodash"
import useResource from "hooks/resources"
import { useAccountId } from "resources/account"

/**
 * Hook to fetch user favorite resources - returns the raw useResource call
 * @returns {Array} [favoriteResourcesData, favoriteResourcesStatus] from useResource
 */
export function useFavoriteResources() {
  const accountId = useAccountId()

  return useResource({
    resource: "userFavoriteResources",
    url: () => accountId ? ({
      id: `userFavoriteResources-${accountId}`,
      url: "/api/users/me/preferences",
      payload: {
        items: [{ type: "favoriteResources", accountId }],
      },
    }) : null,
  }, "userFavoriteResources")
}

/**
 * Hook to check if a single resource is favorited
 * @param {string} resourceId - The ID of the resource to check
 * @returns {Array} [isFavorite: boolean, status: string]
 */
export function useIsResourceFavorite(resourceId) {
  const [favoriteResourcesData, favoriteResourcesStatus] = useFavoriteResources()

  return useMemo(() => {
    if (!resourceId || favoriteResourcesStatus === "loading") {
      return [false, favoriteResourcesStatus]
    }

    if (favoriteResourcesStatus !== "ok") {
      return [false, favoriteResourcesStatus]
    }

    const favoriteResourcesList = get(favoriteResourcesData, "0.value", [])
    const isFavorite = favoriteResourcesList.some(item => item.resourceId === resourceId)
    return [isFavorite, favoriteResourcesStatus]
  }, [resourceId, favoriteResourcesData, favoriteResourcesStatus])
}
